import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useAppSelector } from '../../app/hooks';
import { useSummaryQuery, usePayoutQuery } from '../../services/api';
import { CH, CHCOLOR } from '../../constants/channels';
import { inr, inrShort, num, pct, fmtPct, deltaPct } from '../../utils/format';
import { KpiGrid } from '../../components/common/KpiCard';
import DataCard from '../../components/common/DataCard';
import TableCard from '../../components/common/TableCard';
import QueryState from '../../components/common/QueryState';
import { Shimmer, ShimmerText, KpiSkeleton, ChartSkeleton, TableSkeleton } from '../../components/common/Shimmer';
import { SectionHeading, InsightRow, ChannelChip } from '../../components/common/Bits';
import HBarChart from '../../components/charts/HBarChart';
import LineChart from '../../components/charts/LineChart';
import DonutChart from '../../components/charts/DonutChart';
import RevenueBanner from './RevenueBanner';

/** Turns the flat `[{date, channel, v}]` series into one line per channel. */
function toSeries(byDate) {
  const dates = [...new Set(byDate.map((r) => r.date))].sort();
  return CH.map((c) => {
    const rows = new Map(byDate.filter((r) => r.channel === c.id).map((r) => [r.date, r.v]));
    return {
      name: c.name,
      color: CHCOLOR[c.id],
      data: dates.map((d) => ({ d, v: rows.get(d) || 0 })),
    };
  });
}

/** Stands in for the whole overview: KPI strip, banner, then the chart grid. */
function OverviewSkeleton() {
  return (
    <>
      <KpiSkeleton columns={5} />
      <Shimmer height={92} radius={16} sx={{ mb: 2.25 }} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}><ChartSkeleton height={230} bars={14} /></Grid>
        <Grid size={{ xs: 12, lg: 4 }}><ChartSkeleton height={230} bars={5} /></Grid>
        <Grid size={{ xs: 12, lg: 6 }}><ChartSkeleton height={200} bars={6} /></Grid>
        <Grid size={{ xs: 12, lg: 6 }}><ChartSkeleton height={200} bars={6} /></Grid>
        <Grid size={12}><TableSkeleton columns={7} rows={6} /></Grid>
      </Grid>
    </>
  );
}

export default function OverviewTab() {
  const range = useAppSelector((s) => s.ui.range);
  const summaryQuery = useSummaryQuery({ range });
  const payoutQuery = usePayoutQuery({ range });

  return (
    <QueryState query={summaryQuery} skeleton={<OverviewSkeleton />}>
      {(data) => {
        const a = data.current;
        const prev = data.previous;
        const byChannel = data.byChannel;

        // payout loads alongside; the dashboard renders without waiting for it
        const payout = payoutQuery.data;
        const payoutByChannel = new Map((payout?.channels || []).map((c) => [c.channel, c.payout]));
        const totInf = payout?.totals?.payout ?? 0;
        const pending = payoutQuery.isLoading;

        const revByCh = byChannel.map((c) => ({ k: c.name, v: c.rev, color: CHCOLOR[c.channel] }));
        const discByCh = byChannel.map((c) => ({ k: c.name, v: c.disc, color: CHCOLOR[c.channel] }));
        const ranked = [...byChannel].sort((x, y) => y.rev - x.rev);
        const byMargin = [...byChannel].sort((x, y) => y.margin - x.margin);
        const worst = [...byChannel].filter((c) => c.n > 0).sort((x, y) => x.real - y.real)[0];
        const top = ranked[0];

        return (
          <>
            <KpiGrid
              columns={5}
              items={[
                { label: 'Total Revenue', value: inrShort(a.rev), sub: `${num(a.n)} txns`, accent: 'var(--s1)', delta: deltaPct(a.rev, prev.rev) },
                { label: 'Transactions', value: num(a.n), sub: `Avg ${inr(a.avg)}`, accent: 'var(--s3)', delta: deltaPct(a.n, prev.n) },
                { label: 'Discounts', value: inrShort(a.disc), sub: `${fmtPct(a.discPct)} of MRP`, accent: 'var(--s2)' },
                { label: 'Realization', value: fmtPct(a.real), sub: 'Collected vs MRP', accent: 'var(--s4)' },
                { label: 'Contribution Margin', value: inrShort(a.margin), sub: `${fmtPct(a.marginPct)} of rev`, accent: 'var(--s5)' },
              ]}
            />

            <RevenueBanner agg={a} range={range} influencerTotal={totInf} pending={pending} />

            {top && worst && (
              <InsightRow
                items={[
                  { icon: '🏆', title: `${top.name} leads`, text: `${inrShort(top.rev)} · ${fmtPct(pct(top.rev, a.rev))} of collections.` },
                  { icon: '💰', title: `${byMargin[0].name} best margin`, text: `${inrShort(byMargin[0].margin)} after lab-to-lab cost.` },
                  { icon: '⚠️', title: `${worst.name} lowest realization`, text: `Only ${fmtPct(worst.real)} of MRP collected.` },
                ]}
              />
            )}

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <DataCard stretch title="Revenue by channel" subtitle="Amount collected per channel">
                  <HBarChart items={revByCh} />
                </DataCard>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <DataCard stretch title="Revenue share" subtitle="Contribution to total collections">
                  <Grid container spacing={2.25} sx={{ alignItems: 'center' }}>
                    <Grid size={{ xs: 12, sm: 'auto' }}>
                      <DonutChart items={revByCh} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 'grow' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, fontSize: 12, color: 'var(--ink-2)' }}>
                        {revByCh.map((r) => (
                          <Box key={r.k} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            <Box component="i" sx={{ width: 11, height: 11, borderRadius: '3px', background: r.color }} />
                            {r.k} — <b style={{ marginLeft: 4 }}>{inrShort(r.v)}</b>
                            <Box component="span" sx={{ color: 'var(--muted)' }}>({fmtPct(pct(r.v, a.rev))})</Box>
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </DataCard>
              </Grid>

              <Grid size={12}>
                <DataCard title="Daily revenue trend by channel" subtitle="Collections per day · hover for detail">
                  <LineChart series={toSeries(data.byDate)} height={280} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.5, fontSize: 12, color: 'var(--ink-2)' }}>
                    {CH.map((c) => (
                      <Box key={c.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <Box component="i" sx={{ width: 11, height: 11, borderRadius: '3px', background: CHCOLOR[c.id] }} />
                        {c.name}
                      </Box>
                    ))}
                  </Box>
                </DataCard>
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <DataCard stretch title="Discounts by channel" subtitle="Total discount value">
                  <HBarChart items={discByCh} />
                </DataCard>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <DataCard stretch title="Top tests by revenue" subtitle="All channels">
                  <HBarChart items={data.topTests.slice(0, 8)} color="var(--s1)" />
                </DataCard>
              </Grid>
            </Grid>

            <SectionHeading>Channel comparison</SectionHeading>
            <TableCard>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th className="num">Txns</th>
                    <th className="num">Revenue</th>
                    <th className="num">Share</th>
                    <th className="num">Avg</th>
                    <th className="num">Discount</th>
                    <th className="num">Realization</th>
                    <th className="num">Margin</th>
                    <th className="num">Influencer</th>
                    <th className="num">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((c) => {
                    const inf = payoutByChannel.get(c.channel) ?? 0;
                    return (
                      <tr key={c.channel}>
                        <td><ChannelChip color={CHCOLOR[c.channel]}>{c.name}</ChannelChip></td>
                        <td className="num">{c.n}</td>
                        <td className="num">{inr(c.rev)}</td>
                        <td className="num">{fmtPct(pct(c.rev, a.rev))}</td>
                        <td className="num">{inr(c.avg)}</td>
                        <td className="num disc">{inr(c.disc)}</td>
                        <td className="num">{fmtPct(c.real)}</td>
                        <td className="num">{inr(c.margin)}</td>
                        <td className="num">{pending ? <ShimmerText chars={7} /> : inr(inf)}</td>
                        <td className="num"><b>{pending ? <ShimmerText chars={7} /> : inr(c.margin - inf)}</b></td>
                      </tr>
                    );
                  })}
                  <tr style={{ fontWeight: 700, background: 'var(--card-2)' }}>
                    <td>Total</td>
                    <td className="num">{a.n}</td>
                    <td className="num">{inr(a.rev)}</td>
                    <td className="num">100%</td>
                    <td className="num">{inr(a.avg)}</td>
                    <td className="num">{inr(a.disc)}</td>
                    <td className="num">{fmtPct(a.real)}</td>
                    <td className="num">{inr(a.margin)}</td>
                    <td className="num">{pending ? <ShimmerText chars={7} /> : inr(totInf)}</td>
                    <td className="num">{pending ? <ShimmerText chars={7} /> : inr(a.margin - totInf)}</td>
                  </tr>
                </tbody>
              </table>
            </TableCard>

            {payoutQuery.isError && (
              <Box sx={{ mt: 1.5, fontSize: 12.5, color: 'var(--crit)' }}>
                Influencer payouts couldn't be loaded — the Net column above excludes them.
              </Box>
            )}
          </>
        );
      }}
    </QueryState>
  );
}
